import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Star, Users, Globe, BookOpen } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";
import { RescheduleNotice } from "@/components/RescheduleNotice";
import { useAuth } from "@/contexts/AuthContext";

const HERO_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,fit=contain/YbNar5pQPVHKQvVK/untitled-design-2-ZqJxQXiWXpu0iLYE.png";
const ABOUT_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=768,h=524,fit=crop/YbNar5pQPVHKQvVK/chatgpt-image-jun-9-2026-06_49_19-pm-rjDEESZxtfu46xNz.png";
const TESTIMONIALS_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=600,fit=crop/YbNar5pQPVHKQvVK/untitled-design-3-2267qpmhTAVMl5FJ.png";

const stats = [
  { value: "250+", label: "Learners" },
  { value: "7", label: "Languages" },
  { value: "17", label: "Countries" },
];

const steps = [
  { num: "1", title: "Choose a Language", desc: "Explore our language offerings and find one that interests you." },
  { num: "2", title: "Join a Class", desc: "Participate in free online lessons led by passionate volunteers." },
  { num: "3", title: "Learn & Connect", desc: "Practice your language skills while connecting with learners from around the world." },
  { num: "4", title: "Grow Together", desc: "Build confidence, friendships, and meaningful global connections." },
];

const testimonials = [
  {
    text: "Learning with Virtually Verbal has been amazing! The sessions are fun. The tutor is also great at helping the students to learn and understand everything.",
    author: "Intermediate Spanish Learner",
  },
  {
    text: "I can say just one thing 'Wow!'. I fell in love with learning French. I can confidently say that without Virtually Verbal I could not have come this far and I want to continue my journey.",
    author: "Basic French Learner",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hovering, setHovering] = useState(false);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container mx-auto px-6 py-20 sm:py-28 lg:py-32">
          {user?.role === "student" && (
            <div className="max-w-xl mb-8">
              <RescheduleNotice />
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left copy */}
            <div className="max-w-xl">
              <div className="inline-block bg-[#FBE7C6] border-2 border-[#0A0A0A] rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider mb-6" data-testid="hero-badge">
                Free Language Learning
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mb-5">
                Learn a Language.<br />
                <span className="text-[#3C8DE4]">Connect with the World.</span>
              </h1>

              <p className="text-base sm:text-lg text-[#4A4A4A] leading-relaxed mb-8 max-w-md">
                Free online language learning that brings students, volunteers, and communities together.
              </p>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/register")}
                  onMouseEnter={() => setHovering(true)}
                  onMouseLeave={() => setHovering(false)}
                  className="brutalist-btn-primary flex items-center gap-2"
                  data-testid="start-learning-btn"
                >
                  Join a Class
                  <ArrowRight className={`w-5 h-5 transition-transform ${hovering ? "translate-x-1" : ""}`} />
                </button>
                <button
                  onClick={() => navigate("/join-us")}
                  className="brutalist-btn-secondary"
                  data-testid="become-volunteer-btn"
                >
                  Become a Volunteer
                </button>
              </div>
            </div>

            {/* Right image */}
            <div className="relative hidden lg:block">
              <div className="brutalist-card overflow-hidden rotate-1 hover:rotate-0 transition-transform duration-300">
                <img src={HERO_IMG} alt="Virtually Verbal students" className="w-full h-80 object-contain" loading="eager" />
              </div>
            </div>
          </div>
        </div>

        {/* Decorative blobs */}
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#D6E6FA] rounded-full blur-3xl opacity-30 -z-10" />
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-[#FBE7C6] rounded-full blur-3xl opacity-30 -z-10" />
      </section>

      {/* About strip */}
      <section className="py-16 px-6 bg-white border-y-2 border-[#0A0A0A]">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-5">Language Learning without Borders</h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-6">
                At Virtually Verbal, we connect students and volunteers around the world through free online language learning, fostering cultural exchange, personal growth, and meaningful global connections.
              </p>
              <button onClick={() => navigate("/projects")} className="text-sm font-bold text-[#3C8DE4] hover:underline inline-flex items-center gap-1" data-testid="explore-projects-link">
                Explore our Projects <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Image + Stats */}
            <div className="space-y-6">
              <div className="brutalist-card overflow-hidden">
                <img src={ABOUT_IMG} alt="Language learning without borders" className="w-full h-56 object-cover" loading="lazy" />
              </div>
              <div className="grid grid-cols-3 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="brutalist-card p-6 text-center bg-[#D6E6FA]" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <div className="text-3xl sm:text-4xl font-extrabold mb-1">{s.value}</div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">{s.label}</div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-[#FDFBF7]" data-testid="how-it-works">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">How Does Virtually Verbal Work?</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {steps.map((step) => (
              <div key={step.num} className="text-center" data-testid={`step-${step.num}`}>
                <div className="w-14 h-14 rounded-full bg-[#3C8DE4] border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-4 text-white font-mono font-bold text-xl"
                  style={{ boxShadow: "3px 3px 0px 0px rgba(10,10,10,1)" }}>
                  {step.num}
                </div>
                <h3 className="text-base font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-[#4A4A4A]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-[#FBE7C6] border-y-2 border-[#0A0A0A]" data-testid="testimonials">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">What Our Learners Say</h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6 max-w-5xl mx-auto items-center">
            <div className="hidden md:block md:col-span-1">
              <div className="brutalist-card overflow-hidden">
                <img src={TESTIMONIALS_IMG} alt="Virtually Verbal learners" className="w-full h-auto object-cover" loading="lazy" />
              </div>
            </div>
            <div className="md:col-span-4 grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="brutalist-card p-8 bg-white" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#FBE7C6] text-[#0A0A0A]" />
                  ))}
                </div>
                <p className="text-sm text-[#4A4A4A] leading-relaxed italic mb-5">&ldquo;{t.text}&rdquo;</p>
                <p className="text-xs font-bold uppercase tracking-wider">— {t.author}</p>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-[#0A0A0A] text-white">
        <div className="container mx-auto text-center max-w-xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-5">Ready to Start Your Language Journey?</h2>
          <p className="text-white/60 text-sm mb-8">Join learners & volunteers from around the world</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate("/register")}
              className="bg-[#3C8DE4] text-white border-2 border-white rounded-full font-bold uppercase tracking-wider px-8 py-3 text-sm hover:bg-[#2D7BD4] transition-colors"
              data-testid="cta-register-btn"
            >
              Become a Student
            </button>
            <button
              onClick={() => navigate("/join-us")}
              className="bg-transparent text-white border-2 border-white/40 rounded-full font-bold uppercase tracking-wider px-8 py-3 text-sm hover:border-white hover:bg-white/10 transition-colors"
              data-testid="cta-volunteer-btn"
            >
              Become a Volunteer
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default LandingPage;
